import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function SpaceScene() {
  const canvasRef = useRef(null);
  const scrollRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0, t: 0 });

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scrollRef.current = max > 0 ? window.scrollY / max : 0;
    };

    const onMouse = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseRef.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
      mouseRef.current.t = performance.now();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x020208);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x020208, 0.002);

    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 20);

    const disposables = [];
    const pr = Math.min(window.devicePixelRatio, 2);

    // Field of view: 60° on laptop/desktop. On narrower screens (phones in portrait)
    // widen the vertical FOV so the horizontal view stays close to the desktop one,
    // otherwise the camera crops the ring into a flat band across the screen.
    const REF_ASPECT = 1.6;
    const MAX_BASE_FOV = 100;
    const deg = Math.PI / 180;
    const getBaseFov = () => {
      const aspect = window.innerWidth / window.innerHeight;
      if (aspect >= REF_ASPECT) return 60;
      const halfH = Math.atan(Math.tan(30 * deg) * REF_ASPECT);
      const fov = (2 * Math.atan(Math.tan(halfH) / aspect)) / deg;
      return Math.min(MAX_BASE_FOV, fov);
    };
    // Particle sizes are in screen pixels, so shrink them on small/wide-FOV screens
    // to keep the same fine, dusty look as on desktop (1280x720 at 60° = 1.0).
    const REF_PROJ = 720 / (2 * Math.tan(30 * deg));
    const getPointScale = () => {
      const proj = window.innerHeight / (2 * Math.tan((getBaseFov() / 2) * deg));
      return Math.min(1, Math.max(0.4, proj / REF_PROJ));
    };
    // "Gentle current": ring particles near the cursor drift slightly, opening screen only,
    // laptops/desktops only. x,y = smoothed cursor (NDC), z = strength 0..1, w = aspect.
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    const uCurrent = { value: new THREE.Vector4(0, 0, 0, window.innerWidth / window.innerHeight) };
    const CURRENT_GLSL = `
            {
              vec2 ndc = gl_Position.xy / gl_Position.w;
              vec2 dd = vec2((ndc.x - uCurrent.x) * uCurrent.w, ndc.y - uCurrent.y);
              float r = length(dd);
              float f = exp(-r * r / 0.05) * uCurrent.z * (0.75 + 0.25 * sin(uTime * 0.5 + aPhase));
              vec2 dir = r > 0.0001 ? dd / r : vec2(0.0);
              vec2 off = (dir * 0.5 + vec2(-dir.y, dir.x) * 0.9) * f * 0.03;
              off.x /= uCurrent.w;
              gl_Position.xy += off * gl_Position.w;
            }`;
    // Shared by every particle material.
    const uPRShared = { value: pr * getPointScale() };

    /* ════════════════════════════════════════
       STARFIELD — soft twinkle, multi-layer
       Reduced density for cleaner hero focus
       ════════════════════════════════════════ */
    function createStarField(count, spread, zRange, sizeRange, color) {
      const positions = new Float32Array(count * 3);
      const sizes = new Float32Array(count);
      const offsets = new Float32Array(count);
      const speeds = new Float32Array(count);

      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * spread;
        positions[i * 3 + 1] = (Math.random() - 0.5) * spread;
        positions[i * 3 + 2] = zRange[0] + Math.random() * (zRange[1] - zRange[0]);
        sizes[i] = sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]);
        offsets[i] = Math.random() * Math.PI * 2;
        speeds[i] = 0.3 + Math.random() * 1.2;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1));
      geo.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uPR: uPRShared,
          uWarp: { value: 0 },
          uColor: { value: new THREE.Color(color) },
        },
        vertexShader: `
          attribute float aSize, aOffset, aSpeed;
          uniform float uTime, uPR, uWarp;
          varying float vAlpha;
          varying vec3 vColor;
          uniform vec3 uColor;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            float twinkle = sin(uTime * aSpeed + aOffset) * 0.3 + 0.7;
            vAlpha = twinkle;
            vColor = uColor;
            float s = aSize * uPR * (90.0 / max(-mv.z, 0.1));
            s *= (1.0 + uWarp * 5.0);
            vAlpha = min(vAlpha * (1.0 + uWarp * 0.6), 1.0);
            gl_PointSize = clamp(s, 0.2, 12.0);
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          varying vec3 vColor;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float core = pow(1.0 - d * 2.0, 2.8);
            float soft = pow(1.0 - d * 2.0, 1.0) * 0.15;
            gl_FragColor = vec4(vColor, (core + soft) * vAlpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const points = new THREE.Points(geo, mat);
      scene.add(points);
      disposables.push(geo, mat);
      return mat;
    }

    const starMats = [
      createStarField(4000, 500, [80, -600], [0.12, 0.5], '#b0bce0'),
      createStarField(1800, 350, [50, -550], [0.35, 1.2], '#ccd0ff'),
      createStarField(400, 180, [30, -500], [0.8, 2.8], '#ffffff'),
      createStarField(700, 400, [40, -580], [0.18, 0.7], '#6898d8'),
    ];

    /* ════════════════════════════════════════
       NEBULA RING — larger, closer, more dominant
       ════════════════════════════════════════ */
    const RING_Z = -35;
    const RING_MAJOR = 26;
    const RING_MINOR = 1.4;

    function createNebulaRing() {
      const N = 16000;
      const positions = new Float32Array(N * 3);
      const colors = new Float32Array(N * 3);
      const sizes = new Float32Array(N);
      const phases = new Float32Array(N);

      const palette = [
        new THREE.Color('#9333ea'),
        new THREE.Color('#7c3aed'),
        new THREE.Color('#6366f1'),
        new THREE.Color('#4f46e5'),
        new THREE.Color('#3b82f6'),
        new THREE.Color('#8b5cf6'),
        new THREE.Color('#a78bfa'),
      ];

      for (let i = 0; i < N; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI * 2;
        const coreRatio = Math.pow(Math.random(), 0.5);
        const tubeR = RING_MINOR * coreRatio;
        const x = (RING_MAJOR + tubeR * Math.cos(phi)) * Math.cos(theta);
        const y = (RING_MAJOR + tubeR * Math.cos(phi)) * Math.sin(theta);
        const z = tubeR * Math.sin(phi);

        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;

        const c = palette[Math.floor(Math.random() * palette.length)];
        colors[i * 3] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;

        sizes[i] = 0.15 + Math.random() * 1.5;
        phases[i] = Math.random() * Math.PI * 2;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uPR: uPRShared,
          uCurrent,
          uCamDist: { value: 80 },
        },
        vertexShader: `
          attribute vec3 aColor;
          attribute float aSize, aPhase;
          uniform vec4 uCurrent;
          uniform float uTime, uPR, uCamDist;
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            vec3 p = position;
            p.x += sin(uTime * 0.12 + aPhase) * 0.12;
            p.y += cos(uTime * 0.10 + aPhase * 1.1) * 0.12;
            p.z += sin(uTime * 0.08 + aPhase * 0.7) * 0.06;
            vColor = aColor;
            vAlpha = 0.55 + sin(uTime * 0.25 + aPhase) * 0.15;
            float distFactor = smoothstep(100.0, 15.0, uCamDist);
            vAlpha *= (0.5 + distFactor * 0.5);
            vec4 mv = modelViewMatrix * vec4(p, 1.0);
            float s = aSize * uPR * (200.0 / max(-mv.z, 0.1));
            gl_PointSize = clamp(s, 0.3, 22.0);
            gl_Position = projectionMatrix * mv;${CURRENT_GLSL}
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float core = pow(1.0 - d * 2.0, 2.2);
            float soft = pow(1.0 - d * 2.0, 0.8) * 0.15;
            gl_FragColor = vec4(vColor * 1.5, (core + soft) * vAlpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Points(geo, mat);
      mesh.position.set(0, 0, RING_Z);
      mesh.rotation.x = Math.PI * 0.44;
      scene.add(mesh);
      disposables.push(geo, mat);
      return { mat, mesh };
    }

    function createNebulaHalo() {
      const N = 3000;
      const positions = new Float32Array(N * 3);
      const colors = new Float32Array(N * 3);
      const sizes = new Float32Array(N);
      const phases = new Float32Array(N);
      const palette = [
        new THREE.Color('#6366f1'),
        new THREE.Color('#7c3aed'),
        new THREE.Color('#4f46e5'),
      ];

      for (let i = 0; i < N; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI * 2;
        const spread = RING_MINOR * (1.5 + Math.random() * 3.0);
        const x = (RING_MAJOR + spread * Math.cos(phi)) * Math.cos(theta);
        const y = (RING_MAJOR + spread * Math.cos(phi)) * Math.sin(theta);
        const z = spread * Math.sin(phi);

        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;

        const c = palette[Math.floor(Math.random() * palette.length)];
        colors[i * 3] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;

        sizes[i] = 4.0 + Math.random() * 8.0;
        phases[i] = Math.random() * Math.PI * 2;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uPR: uPRShared, uCurrent },
        vertexShader: `
          attribute vec3 aColor;
          attribute float aSize, aPhase;
          uniform vec4 uCurrent;
          uniform float uTime, uPR;
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            vec3 p = position + sin(uTime * 0.06 + position.x * 0.15) * 0.3;
            vColor = aColor;
            vAlpha = 0.05 + sin(uTime * 0.12 + aPhase) * 0.015;
            vec4 mv = modelViewMatrix * vec4(p, 1.0);
            gl_PointSize = clamp(aSize * uPR * (200.0 / max(-mv.z, 0.1)), 1.0, 55.0);
            gl_Position = projectionMatrix * mv;${CURRENT_GLSL}
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float glow = pow(1.0 - d * 2.0, 3.5);
            gl_FragColor = vec4(vColor, glow * vAlpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Points(geo, mat);
      mesh.position.set(0, 0, RING_Z);
      mesh.rotation.x = Math.PI * 0.44;
      scene.add(mesh);
      disposables.push(geo, mat);
      return { mat, mesh };
    }

    function createLensingArcs() {
      const N = 2000;
      const positions = new Float32Array(N * 3);
      const colors = new Float32Array(N * 3);
      const sizes = new Float32Array(N);
      const phases = new Float32Array(N);

      for (let i = 0; i < N; i++) {
        const theta = Math.random() * Math.PI * 2;
        const r = RING_MAJOR + (Math.random() - 0.5) * 2.0;
        positions[i * 3] = r * Math.cos(theta);
        positions[i * 3 + 1] = r * Math.sin(theta);
        positions[i * 3 + 2] = (Math.random() - 0.5) * 0.6;

        const t = Math.random();
        colors[i * 3] = 0.6 + t * 0.4;
        colors[i * 3 + 1] = 0.7 + t * 0.3;
        colors[i * 3 + 2] = 1.0;

        sizes[i] = 0.12 + Math.random() * 0.5;
        phases[i] = theta;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uPR: uPRShared, uCurrent },
        vertexShader: `
          attribute vec3 aColor;
          attribute float aSize, aPhase;
          uniform vec4 uCurrent;
          uniform float uTime, uPR;
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            vec3 p = position;
            float angle = uTime * 0.03;
            float ca = cos(angle), sa = sin(angle);
            p.xy = mat2(ca, -sa, sa, ca) * p.xy;
            vColor = aColor;
            vAlpha = 0.25 + sin(uTime * 0.4 + aPhase * 2.5) * 0.12;
            vec4 mv = modelViewMatrix * vec4(p, 1.0);
            gl_PointSize = clamp(aSize * uPR * (140.0 / max(-mv.z, 0.1)), 0.2, 6.0);
            gl_Position = projectionMatrix * mv;${CURRENT_GLSL}
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float glow = pow(1.0 - d * 2.0, 1.6);
            gl_FragColor = vec4(vColor * 1.3, glow * vAlpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Points(geo, mat);
      mesh.position.set(0, 0, RING_Z);
      mesh.rotation.x = Math.PI * 0.44;
      scene.add(mesh);
      disposables.push(geo, mat);
      return { mat, mesh };
    }

    function createCenterGlow() {
      const s = 128;

      const c = document.createElement('canvas');
      c.width = c.height = s;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      g.addColorStop(0, 'rgba(110,140,255,0.4)');
      g.addColorStop(0.25, 'rgba(124,58,237,0.15)');
      g.addColorStop(0.55, 'rgba(59,130,246,0.04)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, s, s);
      const tex = new THREE.CanvasTexture(c);
      disposables.push(tex);

      const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(40, 40, 1);
      sprite.position.set(0, 0, RING_Z);
      scene.add(sprite);
      disposables.push(mat);

      const c2 = document.createElement('canvas');
      c2.width = c2.height = s;
      const ctx2 = c2.getContext('2d');
      const g2 = ctx2.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      g2.addColorStop(0, 'rgba(147,51,234,0.15)');
      g2.addColorStop(0.4, 'rgba(99,102,241,0.04)');
      g2.addColorStop(1, 'rgba(0,0,0,0)');
      ctx2.fillStyle = g2;
      ctx2.fillRect(0, 0, s, s);
      const tex2 = new THREE.CanvasTexture(c2);
      disposables.push(tex2);

      const mat2 = new THREE.SpriteMaterial({
        map: tex2,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
      });
      const sprite2 = new THREE.Sprite(mat2);
      sprite2.scale.set(70, 70, 1);
      sprite2.position.set(0, 0, RING_Z);
      scene.add(sprite2);
      disposables.push(mat2);

      return { sprite, sprite2 };
    }

    function createDeepSpaceParticles() {
      const N = 3500;
      const positions = new Float32Array(N * 3);
      const sizes = new Float32Array(N);
      const offsets = new Float32Array(N);

      for (let i = 0; i < N; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 400;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 400;
        positions[i * 3 + 2] = -100 + Math.random() * -400;
        sizes[i] = 0.1 + Math.random() * 0.7;
        offsets[i] = Math.random() * Math.PI * 2;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
      geo.setAttribute('aOffset', new THREE.BufferAttribute(offsets, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uPR: uPRShared },
        vertexShader: `
          attribute float aSize, aOffset;
          uniform float uTime, uPR;
          varying float vAlpha;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            vAlpha = sin(uTime * 0.8 + aOffset) * 0.25 + 0.75;
            gl_PointSize = clamp(aSize * uPR * (60.0 / max(-mv.z, 0.1)), 0.2, 5.0);
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float glow = pow(1.0 - d * 2.0, 1.8);
            gl_FragColor = vec4(0.78, 0.82, 1.0, glow * vAlpha * 0.7);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Points(geo, mat);
      scene.add(mesh);
      disposables.push(geo, mat);
      return mat;
    }

    /* ════════════════════════════════════════
       NEURAL LINES — faint, periphery only
       ════════════════════════════════════════ */
    function createNeuralLines() {
      const NODE_COUNT = 60;
      const ZONE_Z = [-160, -340];
      const nodes = [];

      for (let i = 0; i < NODE_COUNT; i++) {
        nodes.push(
          new THREE.Vector3(
            (Math.random() - 0.5) * 120,
            (Math.random() - 0.5) * 80,
            ZONE_Z[0] + Math.random() * (ZONE_Z[1] - ZONE_Z[0])
          )
        );
      }

      const MAX_DIST = 40;
      const linePositions = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        for (let j = i + 1; j < NODE_COUNT; j++) {
          if (nodes[i].distanceTo(nodes[j]) < MAX_DIST) {
            linePositions.push(
              nodes[i].x, nodes[i].y, nodes[i].z,
              nodes[j].x, nodes[j].y, nodes[j].z
            );
          }
        }
      }

      const lineGeo = new THREE.BufferGeometry();
      lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

      const lineMat = new THREE.LineBasicMaterial({
        color: 0x6366f1,
        transparent: true,
        opacity: 0.025,
        blending: THREE.AdditiveBlending,
      });

      const linesMesh = new THREE.LineSegments(lineGeo, lineMat);
      scene.add(linesMesh);
      disposables.push(lineGeo, lineMat);
      return { lineMat };
    }

    const nebCore = createNebulaRing();
    const nebHalo = createNebulaHalo();
    const lensArcs = createLensingArcs();
    const glows = createCenterGlow();
    const deepStarMat = createDeepSpaceParticles();
    const neural = createNeuralLines();

    /* ════════════════════════════════════════
       CAMERA Z-PATH
       Updated for closer hero feel
       ════════════════════════════════════════ */
    function getCameraZ(p) {
      if (p < 0.12) return 20 + (p / 0.12) * -10;
      if (p < 0.30) return 10 + ((p - 0.12) / 0.18) * -20;
      if (p < 0.45) return -10 + ((p - 0.30) / 0.15) * -20;
      if (p < 0.55) return -30 + ((p - 0.45) / 0.10) * -20;
      if (p < 0.80) return -50 + ((p - 0.55) / 0.25) * -60;
      return -110 + ((p - 0.80) / 0.20) * -50;
    }

    function getWarp(p) {
      if (p < 0.43 || p > 0.58) return 0;
      const center = 0.505;
      const halfWidth = 0.075;
      const dist = Math.abs(p - center) / halfWidth;
      return Math.max(0, 1 - dist * dist) * 1.8;
    }

    const clock = new THREE.Clock();
    let animId;
    let currentZ = 20;
    let baseFov = getBaseFov();
    let currentFov = baseFov;

    function animate() {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const p = scrollRef.current;
      const warp = getWarp(p);
      const targetZ = getCameraZ(p);

      currentZ += (targetZ - currentZ) * 0.04;
      camera.position.z = currentZ;

      camera.position.x += (mouseRef.current.x * 0.5 - camera.position.x) * 0.01;
      camera.position.y += (-mouseRef.current.y * 0.3 - camera.position.y) * 0.01;

      camera.position.x += Math.sin(t * 0.08) * 0.06;
      camera.position.y += Math.cos(t * 0.06) * 0.04;

      camera.lookAt(
        camera.position.x * 0.12,
        camera.position.y * 0.12,
        camera.position.z - 20
      );

      starMats.forEach((m) => {
        m.uniforms.uTime.value = t;
        m.uniforms.uWarp.value = warp;
      });

      const camDist = Math.abs(currentZ - RING_Z);
      nebCore.mat.uniforms.uTime.value = t;
      nebCore.mat.uniforms.uCamDist.value = camDist;
      nebHalo.mat.uniforms.uTime.value = t;
      lensArcs.mat.uniforms.uTime.value = t;
      deepStarMat.uniforms.uTime.value = t;

      nebCore.mesh.rotation.z = t * 0.008;
      nebHalo.mesh.rotation.z = t * 0.005;
      lensArcs.mesh.rotation.z = -t * 0.004;

      neural.lineMat.opacity = 0.02 + Math.sin(t * 0.15) * 0.008;

      glows.sprite.material.opacity = 0.4 + Math.sin(t * 0.25) * 0.12;
      const gs = 40 + Math.sin(t * 0.2) * 2;
      glows.sprite.scale.set(gs, gs, 1);

      glows.sprite2.material.opacity = 0.25 + Math.sin(t * 0.15) * 0.06;
      const gs2 = 70 + Math.sin(t * 0.12) * 3;
      glows.sprite2.scale.set(gs2, gs2, 1);

      // Gentle current: follow the cursor slowly; fade out once scrolling starts
      // or after the cursor has been still for a few seconds.
      const idle = performance.now() - (mouseRef.current.t || 0) > 2500;
      const heroK = Math.max(0, 1 - p / 0.04);
      const targetStrength = hasFinePointer && !idle ? heroK : 0;
      const cur = uCurrent.value;
      cur.x += (mouseRef.current.x - cur.x) * 0.03;
      cur.y += (-mouseRef.current.y - cur.y) * 0.03;
      cur.z += (targetStrength - cur.z) * 0.02;

      const targetFov = Math.min(baseFov + warp * 25, 115);
      currentFov += (targetFov - currentFov) * 0.05;
      camera.fov = currentFov;
      camera.updateProjectionMatrix();

      renderer.render(scene, camera);
    }
    animate();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      baseFov = getBaseFov();
      uPRShared.value = pr * getPointScale();
      uCurrent.value.w = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="space-canvas"
      className="fixed inset-0 w-full h-full"
      style={{ zIndex: 0 }}
    />
  );
}
